import React, {useCallback, useMemo, useState} from 'react';
import {ChevronDown, CircleCheck} from 'lucide-react';
import {getStatusClass} from '@/constants/orderStatus';
import {Link} from 'react-router-dom';
import {useMutation} from '@tanstack/react-query';
import type {StatusTone, StatusToneStyles} from './statusTone';

export type PatientOrdersSortOption = 'recent' | 'oldest' | 'name';

export interface OrderSearchFields {
    oid: string;
    firstName: string;
    lastName: string;
    email: string;
}

export interface PatientOrderRow {
    id: number;
    patient: string;
    panel: string;
    orderDate: string;
    createdDate?: string | null;
    statusLabel: string;
    tone: StatusTone;
    actionLabel: string;
}

export interface PatientOrdersTableProps {
    orders: PatientOrderRow[];
    ordersLoading: boolean;
    ordersError: string | null;
    statusToneStyles: StatusToneStyles;
    onSearchOrders: (
        fields: OrderSearchFields
    ) => Promise<{orders: PatientOrderRow[]; toast: {error: boolean; message: string}}>;
}

const sortOptions: Array<{label: string; value: PatientOrdersSortOption}> = [
    {label: 'Most Recent', value: 'recent'},
    {label: 'Oldest First', value: 'oldest'},
    {label: 'Patient Name (A–Z)', value: 'name'},
];

const PatientOrdersTable: React.FC<PatientOrdersTableProps> = ({
    orders,
    ordersLoading,
    ordersError,
    statusToneStyles,
    onSearchOrders,
}) => {
    const [isSearchPanelOpen, setIsSearchPanelOpen] = useState(false);
    const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
    const [sortOption, setSortOption] = useState<PatientOrdersSortOption>('recent');
    const [searchFields, setSearchFields] = useState<OrderSearchFields>({
        oid: '',
        firstName: '',
        lastName: '',
        email: '',
    });
    const [searchToast, setSearchToast] = useState<{error: boolean; message: string}>({
        error: false,
        message: '',
    });
    const [filteredOrders, setFilteredOrders] = useState<PatientOrderRow[] | null>(null);
    const hasActiveFilters = filteredOrders !== null;

    const createdAtMs = useCallback((value: string | null | undefined) => {
        if (!value) return 0;
        const ms = new Date(value).getTime();
        return Number.isFinite(ms) ? ms : 0;
    }, []);

    const displayedOrders = useMemo(() => {
        const base = filteredOrders ?? orders;
        const output = [...base];

        switch (sortOption) {
            case 'oldest':
                output.sort((a, b) => createdAtMs(a.createdDate) - createdAtMs(b.createdDate));
                break;
            case 'name':
                output.sort((a, b) => a.patient.localeCompare(b.patient));
                break;
            case 'recent':
            default:
                output.sort((a, b) => createdAtMs(b.createdDate) - createdAtMs(a.createdDate));
                break;
        }

        return output;
    }, [createdAtMs, filteredOrders, orders, sortOption]);

    const searchOrdersMutation = useMutation({
        mutationFn: (fields: OrderSearchFields) => onSearchOrders(fields),
        onSuccess: ({orders: searchedOrders, toast}) => {
            setFilteredOrders(searchedOrders);
            setSearchToast(toast);
        },
        onError: () => {
            setSearchToast({error: true, message: 'Error searching orders. Please try again.'});
            setFilteredOrders(null);
        },
    });

    const handleSearchInputChange = useCallback(
        (field: keyof OrderSearchFields) => (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;
            setSearchFields((prev) => ({
                ...prev,
                [field]: value,
            }));
        },
        []
    );

    const handleSearchSubmit = useCallback(
        (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            setSearchToast({error: false, message: ''});
            searchOrdersMutation.mutate(searchFields);
        },
        [searchFields, searchOrdersMutation]
    );

    const handleResetFilters = useCallback(() => {
        setSearchFields({
            oid: '',
            firstName: '',
            lastName: '',
            email: '',
        });
        setFilteredOrders(null);
        setSearchToast({error: false, message: ''});
        setIsSearchPanelOpen(false);
    }, []);

    return (
        <div className="min-w-0 lg:col-span-2">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <h2 className="text-[27px] font-medium text-mld-text-primary mb-2 ">Patient Orders</h2>
                <div className="flex flex-wrap gap-3 text-sm text-mld-muted-text">
                        <button
                            type="button"
                            className="flex items-center gap-1 hover:text-mld-primary"
                            aria-expanded={isSearchPanelOpen}
                            aria-controls="patientOrders-filterPanel"
                            onClick={() => setIsSearchPanelOpen((prev) => !prev)}>
                            Filter by
                            <ChevronDown className="h-4 w-4 text-mld-muted-text" />
                        </button>
                    <div className="relative">
                        <button
                            type="button"
                            className="flex items-center gap-1 hover:text-mld-primary"
                            aria-haspopup="menu"
                            aria-expanded={isSortMenuOpen}
                            aria-controls="patientOrders-sortMenu"
                            onClick={() => setIsSortMenuOpen((prev) => !prev)}>
                            Sort by
                            <ChevronDown className="h-4 w-4 text-mld-muted-text" />
                        </button>
                        {isSortMenuOpen && (
                            <div
                                id="patientOrders-sortMenu"
                                role="menu"
                                aria-label="Sort patient orders"
                                className="absolute right-0 z-10 mt-2 w-46 rounded-2xl border border-mld-panel-border bg-mld-background p-2 shadow-lg">
                                {sortOptions.map((option) => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        role="menuitemradio"
                                        aria-checked={sortOption === option.value}
                                        className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm ${
                                            sortOption === option.value
                                                ? 'bg-mld-menu-background text-mld-primary'
                                                : 'text-mld-text-primary hover:bg-mld-menu-background'
                                        }`}
                                        onClick={() => {
                                            setSortOption(option.value);
                                            setIsSortMenuOpen(false);
                                        }}>
                                        {option.label}
                                        {sortOption === option.value && (
                                            <CircleCheck className="h-4 w-4 text-mld-primary" />
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {isSearchPanelOpen && (
                <div
                    id="patientOrders-filterPanel"
                    role="region"
                    aria-label="Order filters"
                    className="mb-4 rounded-2xl border border-mld-panel-border bg-mld-menu-background p-4">
                    <form onSubmit={handleSearchSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <label
                                    htmlFor="providerSearch-oid"
                                    className="mb-1 block text-xs font-semibold uppercase tracking-wide text-mld-muted-text">
                                    Order ID
                                </label>
                                <input
                                    id="providerSearch-oid"
                                    type="text"
                                    value={searchFields.oid}
                                    onChange={handleSearchInputChange('oid')}
                                    placeholder="Optional"
                                    className="w-full rounded-lg border border-mld-panel-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-mld-primary"
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor="providerSearch-first"
                                    className="mb-1 block text-xs font-semibold uppercase tracking-wide text-mld-muted-text">
                                    First Name
                                </label>
                                <input
                                    id="providerSearch-first"
                                    type="text"
                                    value={searchFields.firstName}
                                    onChange={handleSearchInputChange('firstName')}
                                    placeholder="Optional"
                                    className="w-full rounded-lg border border-mld-panel-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-mld-primary"
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor="providerSearch-last"
                                    className="mb-1 block text-xs font-semibold uppercase tracking-wide text-mld-muted-text">
                                    Last Name
                                </label>
                                <input
                                    id="providerSearch-last"
                                    type="text"
                                    value={searchFields.lastName}
                                    onChange={handleSearchInputChange('lastName')}
                                    placeholder="Optional"
                                    className="w-full rounded-lg border border-mld-panel-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-mld-primary"
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor="providerSearch-email"
                                    className="mb-1 block text-xs font-semibold uppercase tracking-wide text-mld-muted-text">
                                    Email
                                </label>
                                <input
                                    id="providerSearch-email"
                                    type="email"
                                    value={searchFields.email}
                                    onChange={handleSearchInputChange('email')}
                                    placeholder="Optional"
                                    className="w-full rounded-lg border border-mld-panel-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-mld-primary"
                                />
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="flex gap-3">
                                <button
                                    type="submit"
                                    disabled={searchOrdersMutation.isPending}
                                    className="rounded-lg bg-mld-primary px-4 py-2 text-white shadow hover:bg-mld-primary/90 disabled:cursor-not-allowed disabled:opacity-60">
                                    {searchOrdersMutation.isPending ? 'Searching...' : 'Search Orders'}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleResetFilters}
                                    className="rounded-lg border border-mld-panel-border px-4 py-2 text-mld-text-secondary hover:text-mld-primary">
                                    Reset
                                </button>
                            </div>
                            {searchToast.message && (
                                <div
                                    role="status"
                                    aria-live="polite"
                                    className={`rounded-lg px-3 py-2 text-sm text-white ${
                                        searchToast.error ? 'bg-[#F15E87]' : 'bg-[#42D4B0]'
                                    }`}>
                                    {searchToast.message}
                                </div>
                            )}
                        </div>
                    </form>
                </div>
            )}

            {ordersError && <p className="text-sm text-red-600">Error loading orders: {ordersError}</p>}
            {hasActiveFilters && !ordersLoading && (
                <p className="mt-2 text-sm text-mld-muted-text">
                    Displaying {(filteredOrders ?? []).length} filtered order(s).
                </p>
            )}
            <div className="overflow-hidden rounded-2xl border border-mld-panel-border text-mld-text-primary">
                <div className="hidden md:grid md:grid-cols-[1fr_1fr_100px_140px_140px_195px_150px] bg-mld-menu-background border-b border-mld-panel-border">
                    <span className="px-6 py-3">Patient Name</span>
                    <span className="px-6 py-3">Order Detail</span>
                    <span className="px-6 py-3">Type</span>
                    <span className="px-6 py-3">Sample Captured</span>
                    <span className="px-6 py-3">Lab Received</span>
                    <span className="px-6 py-3">Status</span>
                    <span className="px-6 py-3">Action</span>
                </div>
                <div className="">
                    {ordersLoading ? (
                        <div className="flex items-center justify-center px-6 py-8 text-sm text-mld-muted-text bg-mld-background">
                            Loading latest patient orders…
                        </div>
                    ) : displayedOrders.length === 0 ? (
                        <div className="flex items-center justify-center px-6 py-8 text-sm text-mld-muted-text bg-mld-background">
                            No patient orders found.
                        </div>
                    ) : (
                        displayedOrders.map((order, index) => {
                            const toneStyle = statusToneStyles[order.tone];
                            const ToneIcon = toneStyle.icon;

                            return (
                                <div
                                    key={order.id}
                                    className={`flex flex-col gap-4 divide-x divide-mld-panel-border ${
                                        index % 2 === 0 ? 'bg-mld-menu-background' : 'bg-mld-background'
                                    } md:grid md:grid-cols-[1fr_1fr_100px_140px_140px_195px_150px] md:items-stretch md:gap-0`}>
                                    <div className="border-r border-mld-panel-border px-4">
                                        <div className="flex items-center py-2 h-full">
                                            <p className="text-xs uppercase tracking-[0.2em] md:hidden">Patient</p>
                                            <p>{order.patient}</p>
                                        </div>
                                    </div>

                                    <div className="border-r border-mld-panel-border px-4">
                                        <div className="flex items-center h-full">
                                            <p className="text-xs uppercase tracking-[0.2em] md:hidden">Order Detail</p>
                                            <p>{order.panel}</p>
                                        </div>
                                    </div>

                                    <div className="border-r border-mld-panel-border px-4">
                                        <div className="flex items-center h-full">
                                            <p className="text-xs uppercase tracking-[0.2em] md:hidden">Order Detail</p>
                                            <p>ToDo</p> {/* HTK | VP - Need ELIS data for this */}
                                        </div>
                                    </div>

                                    <div className="border-r border-mld-panel-border px-4">
                                        <div className="flex items-center h-full">
                                            <p className="text-xs uppercase tracking-[0.2em] md:hidden">
                                                Sample Captured
                                            </p>
                                            <p>{order.orderDate}</p>
                                        </div>
                                    </div>

                                    <div className="border-r border-mld-panel-border px-4">
                                        <div className="flex items-center h-full">
                                            <p className="text-xs uppercase tracking-[0.2em] md:hidden">Lab Received</p>
                                            <p>{order.orderDate}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 border-r border-mld-panel-border px-4">
                                        <div className="flex items-center h-full">
                                            <p className="text-xs uppercase tracking-[0.2em] md:hidden">Status</p>
                                            <div
                                                className={`flex items-center gap-1 ${getStatusClass(
                                                    order.statusLabel
                                                )}`}>
                                                {order.statusLabel}
                                                {ToneIcon && <ToneIcon className="h-4 w-4" />}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center px-4">
                                        <Link
                                            to={`/result/${order.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={`${order.actionLabel} for ${order.patient}`}
                                            className={`hover:underline ${toneStyle.action}`}>
                                            {order.actionLabel}
                                        </Link>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

export default PatientOrdersTable;
